export const CONTENT_GROUPS = {
  main: 'main',
  sales: 'sales',
  rent: 'rent',
  neighborhoods: 'neighborhoods',
  buildings: 'buildings',
  blog: 'blog',
  account: 'account',
  other: 'other',
  error: 'error',
} as const;

export type ContentGroup = typeof CONTENT_GROUPS[keyof typeof CONTENT_GROUPS];

export type ContactFormLocation =
  | 'header_popup'
  | 'page_top'
  | 'page_popup'
  | 'request_info_block';

export type NewsletterLocation = 'header' | 'footer';

export type TrafficType = 'ppc' | 'organic';

export type DataLayerEvent =
  | { content_group: ContentGroup | undefined }
  | {
      event: 'contact_us';
      form_location: ContactFormLocation;
      user_provided_data: { email: string; phone: string };
    }
  | {
      event: 'newsletter_subscribe';
      form_location: NewsletterLocation;
      user_provided_data: { email: string; phone: string };
    }
  | {
      event: 'registration_successful';
      user_provided_data: { email: string; phone: string };
    }
  | { event: 'click_phone' }
  | {
      event: 'crisp_chat_lead';
      traffic_type: TrafficType;
      user_provided_data: { email: string; phone: string };
    }
  | Record<string, unknown>;

function pushToDataLayer(data: DataLayerEvent): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

export function pushContentGroup(contentGroup: ContentGroup): void {
  pushToDataLayer({ content_group: contentGroup });
}

export function pushContactUsEvent(
  email: string,
  phone: string,
  formLocation: ContactFormLocation
): void {
  pushToDataLayer({
    event: 'contact_us',
    form_location: formLocation,
    user_provided_data: { email, phone },
  });
}

export function pushNewsletterSubscribeEvent(
  email: string,
  phone: string,
  formLocation: NewsletterLocation
): void {
  pushToDataLayer({
    event: 'newsletter_subscribe',
    form_location: formLocation,
    user_provided_data: { email, phone },
  });
}

export type RegistrationMethod = 'email' | 'google';

export function pushRegistrationSuccessfulEvent(
  email: string,
  phone: string,
  method: RegistrationMethod
): void {
  pushToDataLayer({
    event: 'registration_successful',
    method,
    user_provided_data: { email, phone },
  });
}

export function pushClickPhoneEvent(): void {
  pushToDataLayer({ event: 'click_phone' });
}

export function pushCrispChatLeadEvent(
  email: string,
  phone: string,
  trafficType: TrafficType
): void {
  pushToDataLayer({
    event: 'crisp_chat_lead',
    traffic_type: trafficType,
    user_provided_data: { email, phone },
  });
}

export function pushCollectionCreatedEvent(
  collectionCode: string,
  listingsCount: number
): void {
  pushToDataLayer({
    event: 'collection_created',
    collection_code: collectionCode,
    listings_count: listingsCount,
  });
}

export function pushCollectionLinkCopiedEvent(collectionCode: string): void {
  pushToDataLayer({
    event: 'collection_link_copied',
    collection_code: collectionCode,
  });
}

export function pushCollectionOpenedEvent(
  collectionCode: string,
  listingsCount: number
): void {
  pushToDataLayer({
    event: 'collection_opened',
    collection_code: collectionCode,
    listings_count: listingsCount,
  });
}

export function pushCollectionListingClickEvent(
  collectionCode: string,
  listingId: string
): void {
  pushToDataLayer({
    event: 'collection_listing_click',
    collection_code: collectionCode,
    listing_id: listingId,
  });
}
